// SPDX-FileCopyrightText: 2017-2024 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import { ErrorBoundary } from '@sentry/react'
import type { ReactNode } from 'react'
import React, { useCallback, useContext } from 'react'
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components'
import { Redirect } from 'wouter'

import { useRegisterScrollContainer } from 'lib-common/utils/scrolling'
import {
  Notifications,
  NotificationsContextProvider
} from 'lib-components/Notifications'
import { EnvironmentLabel } from 'lib-components/atoms/EnvironmentLabel'
import SkipToContent from 'lib-components/atoms/buttons/SkipToContent'
import { desktopMin, zoomedMobileMax } from 'lib-components/breakpoints'
import ErrorPage from 'lib-components/molecules/ErrorPage'
import { LoginErrorModal } from 'lib-components/molecules/modals/LoginErrorModal'
import { featureFlags } from 'lib-customizations/citizen'
import { theme } from 'lib-customizations/common'

import { useChildrenStartingNotification } from './ChildStartingNotificationHook'
import { UnwrapResult } from './async-rendering'
import SessionExpiry from './auth/SessionExpiry'
import { AuthContext, AuthContextProvider, useUser } from './auth/state'
import { Localization, useTranslation } from './localization'
import { MessageDraftsProvider } from './messages/drafts'
import Header from './navigation/Header'
import MobileNav from './navigation/MobileNav'
import { headerHeightMobile, mobileBottomNavHeight } from './navigation/const'
import GlobalDialog from './overlay/GlobalDialog'
import { OverlayContextProvider } from './overlay/state'
import { InstallSuggestion } from './pwa/InstallSuggestion'
import { PushSuggestion } from './pwa/PushSuggestion'
import { useAppBadge } from './pwa/appBadge'
import { useStandaloneLayout } from './pwa/installed'
import { useNotificationClickRouting } from './pwa/notificationRouting'
import { queryClient, QueryClientProvider } from './query'

// Scrolling an element into view must leave it clear of the sticky header and
// the fixed mobile navi. In PWA they do not overlay the scrolling ScrollArea,
// so the document's scroll padding does not matter there.
const GlobalStyle = createGlobalStyle`
  html {
    scroll-padding-top: ${headerHeightMobile}px;
    scroll-padding-bottom: ${mobileBottomNavHeight}px;

    @media (min-width: ${desktopMin}) {
      scroll-padding-top: 0;
      scroll-padding-bottom: 0;
    }
  }

  @media screen and (max-width: ${zoomedMobileMax}) {
    html {
      overflow-x: auto;
    }
  }
`

export function App({ children }: { children: React.ReactNode }) {
  const i18n = useTranslation()

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <ErrorBoundary
          fallback={() => <ErrorPage basePath="/" labels={i18n.errorPage} />}
        >
          <AuthContextProvider>
            <Localization>
              <OverlayContextProvider>
                <NotificationsContextProvider>
                  <MessageDraftsProvider>
                    <Content>{children}</Content>
                    <GlobalDialog />
                    <LoginErrorModal />
                    <div id="modal-container" />
                    <div id="datepicker-container" />
                    <div id="tooltip-container" />
                  </MessageDraftsProvider>
                </NotificationsContextProvider>
              </OverlayContextProvider>
            </Localization>
          </AuthContextProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

// The app has the following DOM structure:
//
// <AppShell>
//   <Header />
//   (...other fixed elements, e.g. notifications...)
//   <ScrollArea (flex-grow)>
//     (...content...)
//   </ScrollArea>
//   <MobileNav />
// </AppShell>
//
// Normally the document scrolls, header and other fixed elements stick to the
// top and the mobile navi is fixed to the bottom.
//
// In PWA (standalone), AppShell is a flex column that fills the screen, and
// ScrollArea is the only scrollable element. The navi is not anchored with
// `position: fixed` or `position: sticky`, because those cause subtle layout
// bugs in iOS PWA. The header must still be `position: sticky`, because iOS 27
// blurs the top of the screen unless a fixed or sticky element is there. It
// never actually sticks, because AppShell does not scroll.
//
const AppShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  html[data-standalone] & {
    height: 100%;
    min-height: auto;
    position: relative;
    overflow: hidden;

    @media print {
      display: block;
      height: auto;
      overflow: visible;
    }
  }
`

const ScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;

  html[data-standalone] & {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: scroll;
    overscroll-behavior: contain;

    @media screen and (max-width: ${zoomedMobileMax}) {
      overflow-x: auto;
    }

    @media print {
      overflow: visible;
    }
  }
`

const FullPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`

const Content = React.memo(function Content({
  children
}: {
  children: React.ReactNode
}) {
  const t = useTranslation()
  const { apiVersion } = useContext(AuthContext)

  useChildrenStartingNotification()
  useStandaloneLayout()
  const scrollAreaRef = useRegisterScrollContainer()
  useNotificationClickRouting()
  useAppBadge()
  return (
    <AppShell>
      <SkipToContent target="main">{t.skipLinks.mainContent}</SkipToContent>
      <Header />
      <InstallSuggestion />
      <PushSuggestion />
      <Notifications apiVersion={apiVersion} sticky offsetTop />
      <ScrollArea ref={scrollAreaRef} data-qa="scroll-area">
        <FullPageContainer>
          <MainContainer>{children}</MainContainer>
        </FullPageContainer>
      </ScrollArea>
      <MobileNav />
      <SessionExpiry />
      {!!featureFlags.environmentLabel && (
        <EnvironmentLabel>{featureFlags.environmentLabel}</EnvironmentLabel>
      )}
    </AppShell>
  )
})

const MainContainer = React.memo(function MainContainer({
  children
}: {
  children: ReactNode
}) {
  const { user } = useContext(AuthContext)
  const render = useCallback(() => <>{children}</>, [children])
  return (
    <ScrollableMain>
      <UnwrapResult result={user}>{render}</UnwrapResult>
    </ScrollableMain>
  )
})

export function HandleRedirection() {
  const user = useUser()

  if (!user) {
    return <Redirect replace to="/login" />
  }

  const fallbackUrl = user.authLevel !== 'STRONG' ? '/map' : '/applications'
  const hasAccessToCalendar = !!user?.accessibleFeatures.reservations
  return hasAccessToCalendar ? (
    <Redirect replace to="/calendar" />
  ) : (
    <Redirect replace to={fallbackUrl} />
  )
}

const ScrollableMain = styled.div`
  flex-grow: 1;

  padding-bottom: ${mobileBottomNavHeight}px;
  @media (min-width: ${desktopMin}) {
    padding-bottom: 0;
  }

  html[data-standalone] & {
    padding-bottom: 0;
  }
`
